import { useRef, useState } from "react";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";

export function ImageUploader({ value = [], onChange, multiple = true, maxImages = 15, recommended = 10, testId = "image-uploader" }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // { done, total }
  const images = value || [];
  const atLimit = multiple && images.length >= maxImages;

  const MAX_BYTES = 19 * 1024 * 1024; // 19MB
  const MIN_W = 2048, MIN_H = 1080;

  const readDimensions = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  const validate = async (file) => {
    if (file.size > MAX_BYTES) return `is ${(file.size / 1048576).toFixed(1)}MB — max allowed is 19MB`;
    const dim = await readDimensions(file);
    if (dim && (dim.w < MIN_W || dim.h < MIN_H)) {
      return `is ${dim.w}×${dim.h}px — minimum is ${MIN_W}×${MIN_H}px (ideal 4000×3000px)`;
    }
    return null;
  };

  const handleFiles = async (files) => {
    let list = Array.from(files || []);
    if (list.length === 0) return;
    if (multiple) {
      const room = maxImages - images.length;
      if (room <= 0) { toast.error(`You can upload up to ${maxImages} photos`); return; }
      if (list.length > room) {
        toast.message(`Only ${room} more photo${room > 1 ? "s" : ""} can be added`);
        list = list.slice(0, room);
      }
    } else {
      list = list.slice(0, 1);
    }

    // Validate resolution + size before uploading
    const valid = [];
    for (const f of list) {
      const problem = await validate(f);
      if (problem) toast.error(`${f.name} ${problem}`);
      else valid.push(f);
    }
    list = valid;
    if (list.length === 0) { if (inputRef.current) inputRef.current.value = ""; return; }

    setProgress({ done: 0, total: list.length });
    const uploaded = [];
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      try {
        const form = new FormData();
        form.append("file", list[i]);
        // Let the browser/axios set the multipart boundary automatically.
        const { data } = await api.post("/upload", form);
        uploaded.push(data.url);
        // Add incrementally so successful uploads persist even if a later one fails.
        onChange(multiple ? [...images, ...uploaded] : uploaded.slice(0, 1));
      } catch (err) {
        failed += 1;
        const msg = formatApiErrorDetail(err.response?.data?.detail) || "Upload failed";
        toast.error(`${list[i].name}: ${msg}`);
      }
      setProgress({ done: i + 1, total: list.length });
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = "";
    if (uploaded.length) toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded${failed ? `, ${failed} failed` : ""}`);
  };

  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));
  const uploading = progress !== null;

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        multiple={multiple}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="flex flex-wrap gap-3">
        {images.map((src, idx) => (
          <div key={`${src}-${idx}`} className="relative group w-24 h-24 rounded-xl overflow-hidden border border-[#EBEBEB] bg-[#F0F0F4]">
            <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
            {idx === 0 && multiple && (
              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">Cover</span>
            )}
            <button
              type="button"
              data-testid={`${testId}-remove-${idx}`}
              onClick={() => removeAt(idx)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {!atLimit && (
          <button
            type="button"
            data-testid={`${testId}-trigger`}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="w-24 h-24 rounded-xl border-2 border-dashed border-[#DDDDDD] flex flex-col items-center justify-center gap-1 text-[#717171] hover:border-[#FF385C] hover:text-[#FF385C] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : (images.length ? <Upload className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />)}
            <span className="text-[11px]">{uploading ? `${progress.done}/${progress.total}` : (images.length ? "Add more" : "Upload")}</span>
          </button>
        )}
      </div>
      {multiple && (
        <p className="text-xs mt-2" data-testid={`${testId}-count`}>
          <span className={images.length >= recommended ? "text-emerald-600 font-medium" : "text-[#FF385C] font-medium"}>
            {images.length} / {maxImages} photos
          </span>
          {images.length < recommended && (
            <span className="text-[#717171]"> · add at least {recommended} for a great listing</span>
          )}
        </p>
      )}
    </div>
  );
}

export default ImageUploader;
